/**
 * Light/dark theme state for the app.
 *
 * The design system already ships both themes — light values on `:root`, dark
 * on `[data-theme="dark"]` — so this owns *which one is active*, not what the
 * colours are. Nothing here defines a colour; a board that looks wrong in light
 * mode is a component reaching past a token, and the fix belongs there.
 *
 * Two places need the answer, and they must agree:
 *
 * 1. `<html data-theme>` — so the page background (`body class="bg-bg"`) and
 *    native chrome (scrollbars, form controls, via `color-scheme`) resolve
 *    correctly *before* React mounts. `index.html` sets this in a boot script
 *    to avoid a flash of the wrong theme; this provider keeps it in sync after.
 * 2. The DS `<ThemeProvider theme>` — so components and portaled overlays
 *    (menus, dialogs, tooltips) inherit the same scope.
 *
 * Until someone picks a side, NOVA follows the operating system and keeps
 * following it — including a change made while the tab is open. An explicit
 * choice is stored and then wins permanently, because someone who reached for
 * the toggle has said something about *this* app, not about their OS.
 */

import { ThemeProvider } from "@norican/react";
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
} from "react";

import { logger } from "@/lib/logger";

export type Theme = "light" | "dark";

/** Where an explicit choice is remembered. Shared with the boot script in `index.html`. */
export const THEME_STORAGE_KEY = "nova.theme";

/** The media query that reports the operating system's preference. */
const DARK_QUERY = "(prefers-color-scheme: dark)";

interface ThemeContextValue {
  /** The theme actually in effect. */
  theme: Theme;
  /** Switch themes and remember it. */
  setTheme: (theme: Theme) => void;
  /** True while no explicit choice has been made, so the OS is still in charge. */
  followsSystem: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * The active theme, plus a setter.
 *
 * @throws If called outside `<AppThemeProvider>` — a component reading the
 *   theme from nowhere would silently render against the wrong one.
 */
export function useTheme(): ThemeContextValue {
  const value = use(ThemeContext);
  if (!value) {
    throw new Error("useTheme must be used inside <AppThemeProvider>");
  }
  return value;
}

/**
 * Owns the light/dark decision, mirrors it onto `<html>`, and applies it.
 *
 * Renders the DS `ThemeProvider` rather than replacing it: this decides *which*
 * theme, the design system applies it — including to portaled overlays.
 */
export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<Theme | null>(readStoredTheme);
  const systemTheme = useSyncExternalStore(
    subscribeToSystemTheme,
    readSystemTheme,
    // No `window` (a test renderer, a future SSR pass): dark is what NOVA
    // shipped with, so it is the least surprising fallback.
    () => "dark" as Theme,
  );

  const theme = choice ?? systemTheme;

  useEffect(() => {
    // Keep `<html>` authoritative for the page background and native chrome.
    // The boot script set this before first paint; this covers every change
    // after, including the OS flipping under a tab that is already open.
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setChoice(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch (error) {
      // Private browsing, a full quota, or storage blocked by policy. The
      // theme still changes for this session; it just will not be remembered.
      logger.warn("[theme] could not persist the theme choice", error);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, followsSystem: choice === null }}>
      <ThemeProvider theme={theme} density="comfortable">
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

/** Read a previously stored choice, or null to follow the OS. */
function readStoredTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Storage can throw outright when blocked by policy — treat it as "no
    // preference stored" rather than letting it take the app down.
    return null;
  }
}

/** The operating system's current preference. */
function readSystemTheme(): Theme {
  return window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

/** Subscribe to OS preference changes for `useSyncExternalStore`. */
function subscribeToSystemTheme(onStoreChange: () => void): () => void {
  const query = window.matchMedia(DARK_QUERY);
  query.addEventListener("change", onStoreChange);
  return () => query.removeEventListener("change", onStoreChange);
}
